/**
 * The Memory control's legend (D340): the layers with something in them and the topics, each a way to focus a group.
 *
 * Ported from RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md), brain/_core.js and
 * brain/port.css (`#timeline-legend`, the HUD legend): a row of chips along the foot, each with its count. Pressing one
 * lights that group, hubs and folders included, and steps the rest back; pressing it again clears it. (Hiding a layer
 * outright is the eye in Find.) One row, so it never covers the panels at either edge. A chip says what it is in words
 * and its count, so colour is never the only signal; the focused chip says so to a screen reader.
 */
import { Button, Popover } from '@heroui/react';

import { type ArmsGrouping, armsGroups } from './armsGroupingModel.js';
import { type GroupFocus, isFocused } from './memoryFocusModel.js';
import type { GlobeColours } from './memoryGlobeColours.js';
import { type Globe, type GlobeLayer, topicsLine } from './memoryGlobeModel.js';

export interface MemoryGlobeLegendProps {
  globe: Globe;
  colours: GlobeColours;
  /** Nine tiers, or five themes: one chip each. */
  grouping: ArmsGrouping;
  /** Layers switched off in Find: their chips say so. */
  offLayers: ReadonlySet<GlobeLayer>;
  focus: GroupFocus | null;
  onFocus: (next: GroupFocus) => void;
  /** The room the docked panels and the stage's own controls take at either edge. */
  insetLeft: number;
  insetRight: number;
  /** A narrow stage (under 900 px): the legend is one chip that opens it, so nothing sits over the picture. */
  chip?: boolean;
}

export function MemoryGlobeLegend({ globe, colours, grouping, offLayers, focus, onFocus, insetLeft, insetRight, chip }: MemoryGlobeLegendProps) {
  const topicFocused = focus?.layer === 'memory' && focus.dept !== undefined;
  if (chip) return <LegendChip globe={globe} colours={colours} grouping={grouping} offLayers={offLayers} focus={focus} onFocus={onFocus} insetLeft={insetLeft} />;
  return (
    <section
      className="arms-panel absolute bottom-3 z-[5] flex min-w-0 flex-wrap items-center gap-0.5 rounded-2xl p-1"
      style={{ left: insetLeft + 12, right: insetRight + 12 + 250, maxWidth: 'fit-content' }}
      aria-label="Legend: press a layer or a topic to focus it"
      data-memory-globe-legend
    >
      {armsGroups(grouping)
        .map((g) => ({ g, count: g.tiers.reduce((n, t) => n + globe.counts[t], 0) }))
        .filter((x) => x.count > 0)
        .map(({ g, count }) => {
          const entry: GroupFocus = g.theme ? { theme: g.theme } : { layer: g.tiers[0] };
          return <Chip key={g.id} colour={colours.layer[g.tiers[0]]} label={g.title} count={count} off={g.tiers.every((t) => offLayers.has(t as GlobeLayer))} focused={isFocused(focus, entry)} onPress={() => onFocus(entry)} />;
        })}
      {globe.departments.length > 0 ? (
        <Popover>
          <Button size="sm" variant="ghost" className={`h-7 min-h-0 shrink-0 gap-1.5 rounded-full px-2.5 text-[12px] font-normal ${topicFocused ? 'bg-white/10' : ''}`} data-memory-globe-topics>
            <span className="flex -space-x-1" aria-hidden="true">
              {globe.departments.slice(0, 4).map((d) => (
                <span key={d.key} className="size-2 rounded-full ring-1 ring-black/30" style={{ background: colours.dept[d.index] }} />
              ))}
            </span>
            <span className="text-foreground">{topicFocused ? (globe.departments[focus!.dept!]?.label ?? 'Topics') : 'Topics'}</span>
            <span className="text-xs tabular-nums text-muted">{globe.topics.total.toLocaleString()}</span>
          </Button>
          <Popover.Content placement="top start" className="w-64 max-w-[calc(100vw-1rem)]">
            <Popover.Dialog aria-label="Which topic of memory to focus" className="flex max-h-[min(24rem,60vh)] flex-col gap-0.5 overflow-y-auto p-1.5" data-memory-scroll>
              <p className="m-0 px-2 py-1 text-xs text-muted" data-memory-topics-line>
                {topicsLine(globe.topics)}
              </p>
              {globe.departments.map((d) => (
                <Chip key={d.key} colour={colours.dept[d.index]} label={d.label} count={d.count} off={offLayers.has('memory')} focused={isFocused(focus, { layer: 'memory', dept: d.index })} onPress={() => onFocus({ layer: 'memory', dept: d.index })} wide />
              ))}
            </Popover.Dialog>
          </Popover.Content>
        </Popover>
      ) : null}
    </section>
  );
}

function Chip({ colour, label, count, off, focused, onPress, wide }: { colour: string; label: string; count: number; off: boolean; focused: boolean; onPress: () => void; wide?: boolean }) {
  const button = (
    <Button
      size="sm"
      variant="ghost"
      className={`h-7 min-h-0 shrink-0 gap-1.5 px-2.5 text-[12px] font-normal ${wide ? 'w-full justify-start rounded-lg' : 'rounded-full'} ${focused ? 'bg-white/10' : ''}`}
      onPress={onPress}
      aria-pressed={focused}
      aria-label={`${label}, ${count} ${count === 1 ? 'item' : 'items'}${off ? ', hidden' : ''}${focused ? ', focused' : ''}`}
    >
      <span className="size-2 shrink-0 rounded-full" style={{ background: colour, opacity: off ? 0.3 : 1, boxShadow: focused ? `0 0 8px ${colour}` : undefined }} />
      <span className={`${wide ? 'min-w-0 flex-1 truncate text-left' : ''} ${off ? 'text-muted line-through' : 'text-foreground'}`} title={label}>
        {label}
      </span>
      <span className="text-xs tabular-nums text-muted">{count.toLocaleString()}</span>
    </Button>
  );
  return button;
}

/** The legend as one chip: "Legend" with the colours of its layers, opening the same chips and topics in a popover. */
function LegendChip({ globe, colours, grouping, offLayers, focus, onFocus, insetLeft }: Pick<MemoryGlobeLegendProps, 'globe' | 'colours' | 'grouping' | 'offLayers' | 'focus' | 'onFocus' | 'insetLeft'>) {
  const groups = armsGroups(grouping)
    .map((g) => ({ g, count: g.tiers.reduce((n, t) => n + globe.counts[t], 0) }))
    .filter((x) => x.count > 0);
  return (
    <section className="absolute bottom-3 z-[5]" style={{ left: insetLeft + 12 }} aria-label="Legend" data-memory-globe-legend data-memory-legend-chip>
      <Popover>
        <Button size="sm" variant="ghost" className="arms-panel h-8 gap-1.5 rounded-full px-3 text-[12px] font-normal text-foreground">
          <span className="flex -space-x-1" aria-hidden="true">
            {groups.slice(0, 5).map(({ g }) => (
              <span key={g.id} className="size-2 rounded-full ring-1 ring-black/30" style={{ background: colours.layer[g.tiers[0]] }} />
            ))}
          </span>
          Legend
        </Button>
        <Popover.Content placement="top start" className="w-64 max-w-[calc(100vw-1rem)]">
          <Popover.Dialog aria-label="Legend: press a layer or a topic to focus it" className="flex max-h-[min(26rem,60vh)] flex-col gap-0.5 overflow-y-auto p-1.5" data-memory-scroll>
            {groups.map(({ g, count }) => {
              const entry: GroupFocus = g.theme ? { theme: g.theme } : { layer: g.tiers[0] };
              return <Chip key={g.id} wide colour={colours.layer[g.tiers[0]]} label={g.title} count={count} off={g.tiers.every((t) => offLayers.has(t as GlobeLayer))} focused={isFocused(focus, entry)} onPress={() => onFocus(entry)} />;
            })}
            {globe.departments.length > 0 ? <p className="m-0 px-2 pb-0.5 pt-2 text-xs font-semibold uppercase tracking-wide text-muted" data-memory-topics-line>{topicsLine(globe.topics)}</p> : null}
            {globe.departments.map((d) => (
              <Chip key={d.key} wide colour={colours.dept[d.index]} label={d.label} count={d.count} off={offLayers.has('memory')} focused={isFocused(focus, { layer: 'memory', dept: d.index })} onPress={() => onFocus({ layer: 'memory', dept: d.index })} />
            ))}
          </Popover.Dialog>
        </Popover.Content>
      </Popover>
    </section>
  );
}
